import React, { useState } from "react"
import {
  Autocomplete,
  TextField,
  Paper,
  Chip,
  Button
} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { makeStyles } from "@mui/styles"

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
  },
  inputRoot: {
    flexWrap: "wrap",
    alignItems: "flex-start",
    padding: 0,
  },
  input: {
    minWidth: "100%",
    fontSize: 16,
  },
  tag: {
    flex: "1 1 100%",
    marginRight: 0,
    marginBottom: 2,
    fontSize: 14,
    justifyContent: "space-between",
  },
  popupIndicator: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  endAdornment: {
    alignItems: "flex-end",
    bottom: "-20px",
    top: "unset",
  },
  listbox: {
    maxHeight: 250,
    overflowY: "auto",
    padding: 0,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#c1c1c1",
      borderRadius: "4px",
    },
  },
  option: {
    fontSize: 16,
    padding: "8px 16px",
  },
  clearButton: {
    width: "100%",
    justifyContent: "flex-start",
    padding: "8px 16px",
    textTransform: "none",
    textDecoration: "underline",
    fontSize: "15px",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  dropdownIcon: {
    position: "absolute",
    right: 4,
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: "#757575",
  },
}))

const FilterSelectAndAutocomplete = ({
  value,
  onChange,
  options = [],
  placeholder = "Rechercher",
  multiple = false
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)

  // Handle the Enter key press inside the Autocomplete input
  const handleEnterKey = (e, inputValue) => {
    if (e.key === "Enter") {
      e.preventDefault() // Prevent default form submission behavior

      const trimmed = inputValue.trim() // Remove surrounding whitespace from input

      // Find all options that match the input (case-insensitive)
      // We use toString() to handle both strings and numbers safely
      const matchingOptions = options.filter(option =>
        option?.toString().toLowerCase().includes(trimmed.toLowerCase())
      )

      if (matchingOptions.length === 1) {
        // If exactly one match, select it automatically
        onChange(
          multiple
            ? [...(value || []), matchingOptions[0]]
            : matchingOptions[0]
        )
        setOpen(false) // Close the dropdown
      } else if (trimmed !== "") {
        // Otherwise, allow free-text input if not empty
        onChange(
          multiple
            ? [...(value || []), trimmed]
            : trimmed
        )
        
        setOpen(false)
      }
    }
  }

  const CustomPaper = (props) => (
    <Paper {...props}>
      {props.children}
      <Button
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onChange(multiple ? [] : "")
          setOpen(false)
        }}
        className={classes.clearButton}
        disabled={!value || (Array.isArray(value) && value.length === 0)}
      >
        Effacer
      </Button>
    </Paper>
  )

  return (
    <div className={classes.root}>
      <Autocomplete
        multiple={multiple}
        freeSolo={!multiple}
        popupIcon={multiple ? <ArrowDropDownIcon /> : null}
        disableClearable
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        value={value || (multiple ? [] : "")}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newValue) => {
          if (!multiple && !options.includes(newValue)) {
            onChange(newValue)
          }
        }}
        PaperComponent={CustomPaper}
        classes={{
          popupIndicator: classes.popupIndicator,
          endAdornment: classes.endAdornment,
          listbox: classes.listbox,
          option: classes.option,
          inputRoot: classes.inputRoot,
          input: classes.input,
          tag: classes.tag,
        }}
        renderTags={(tagValue, getTagProps) =>
          multiple
            ? tagValue.map((option, index) => (
                <Chip key={option} label={option} {...getTagProps({ index })} className={classes.tag} />
              ))
            : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            placeholder={placeholder}
            fullWidth
            onKeyDown={(e) => handleEnterKey(e, params.inputProps.value)}
            InputProps={{
              ...params.InputProps,
              style: {
                fontSize: 14,
                lineHeight: "22px",
                fontWeight: 400,
                paddingRight: 32,
              },
            }}
          />
        )}
      />
      {!multiple && (
        <ArrowDropDownIcon
          onClick={() => setOpen(prev => !prev)}
          className={classes.dropdownIcon}
          style={{
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`
          }}
        />
      )}
    </div>
  )
}

export default FilterSelectAndAutocomplete